import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  inject,
  Input,
  ViewChild
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

import { ContextMenuComponent, ContextSubmenuComponent, IContextMenuOption, ImageUrlService } from '@cinetixx/cinetixx-ui';
import { AuditoriumShapeDirective } from '../../core/directives/auditorium-shape.directive';
import { DragStates } from '../../core/enums';
import { IImageElement } from '../../core/interfaces';
import { ImageManager, ShapeAttributesManager } from '../../core/managers';
import { ImageElementsApiService } from '../../core/services/api-services';
import { CreatorActionsService, CreatorStateService, ResizeService } from '../../core/services/app-services';
import { GuidWrapper } from '../../core/types';
import { CX_CORE_STANDALONE } from '../../../shared/cx-core-standalone';
@Component({
  imports: [ ...CX_CORE_STANDALONE, ContextMenuComponent, ContextSubmenuComponent, NgTemplateOutlet ],
  selector: 'app-image-element',
  templateUrl: './image-element.component.html',
  styleUrls: ['./image-element.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'shape draggable'
  },
  hostDirectives: [
    {
      directive: AuditoriumShapeDirective,
      inputs: ['componentRef']
    }
  ]
})
export class ImageElementComponent implements AfterViewInit {

  @ViewChild('alignSubmenu') contextSubmenuComponent: ContextSubmenuComponent;
  @ViewChild(ContextMenuComponent) contextMenuComponent: ContextMenuComponent;

  @Input() set dataObject(value: GuidWrapper<IImageElement>) {
    if (value) {
      this._dataObject = value;

      this.width = `${value.w}px`;
      this.height = `${value.h}px`;
      this.x = `${value.x}px`;
      this.y = `${value.y}px`;
      this.layer = value.layer;
      this.id = value.id;
      this.lock = value.lock;

      this._cdr.detectChanges();
    }
  }

  @Input() showContextMenu = true;

  @HostBinding('style.position') @Input() position: string;
  @HostBinding('attr.draggable') @Input() draggable = false;
  @HostBinding('style.left') @Input() x: string;
  @HostBinding('style.top') @Input() y: string;
  @HostBinding('style.width') @Input() width: string;
  @HostBinding('style.height') @Input() height: string;
  @HostBinding('attr.id') @Input() id: number;
  @HostBinding('attr.data-drag-id') @Input() dragId: string;
  @HostBinding('attr.data-block') @Input() lock: boolean;
  @HostBinding('style.z-index') @Input() layer: number;

  public shapeOptions: IContextMenuOption[] = [];
  public alignSubmenuOptions: IContextMenuOption[] = [];
  public readonly auditoriumShapeDirective = inject(AuditoriumShapeDirective, { host: true });

  private _resizeService: ResizeService;
  private _dataObject: GuidWrapper<IImageElement>;

  private readonly _elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _imageElementsApiService = inject(ImageElementsApiService);
  private readonly _imageUrlService = inject(ImageUrlService);

  public ngOnInit(): void {
    this._resizeService = new ResizeService(this._destroyRef);
  }

  public get imageSrc(): string {
    return this._dataObject.imageId ? this._imageUrlService.generateAuditoriumBackgroundUrl(`${this._dataObject.imageId}`) : null;
  }

  public get elementRef(): ElementRef<HTMLElement> {
    return this._elementRef;
  }

  public ngAfterViewInit(): void {
    this._resizeService.createResizePoints(this._elementRef.nativeElement);
  }

  public get dataObject(): GuidWrapper<IImageElement> {
    return this._dataObject;
  }

  public open(event: MouseEvent): void {
    if (CreatorStateService.currentStep !== 'seats-plan') {
      return;
    }

    ShapeAttributesManager.setDragState(this._elementRef.nativeElement, DragStates.SELECTED);

    this.shapeOptions = [
      {
        title: '<i class="ri-file-copy-2-line m-r-8"></i>' + 'Copy'.trans('auditoriums/shape/context-menu/copy/option.Text'),
        action: () => CreatorStateService.shapesToCopy = [this._elementRef.nativeElement],
        isHidden: this.lock
      },
      {
        title: '<i class="ri-star-line m-r-8"></i>' + 'Add to element library'.trans('auditoriums/shape/context-menu/add-to-library/option.Text'),
        action: () => this._imageElementsApiService.addToLibrary(this._dataObject.imageId).subscribe(() => {
          this._imageElementsApiService.getLibraryImageElements().subscribe();
        }),
        isHidden: !!CreatorStateService.libraryImageElements.find(x => x.imageId === this._dataObject.imageId) || this.lock
      },
      {
        title: '<div class="d-flex justify-content-between full-width">' + '<div>' + '<i class="ri-stack-line m-r-8"></i>' + 'Align'.trans('auditoriums/shape/context-menu/align/option.Text') + '</div>' + '<i class="ri-arrow-right-s-line"></i>' + '</div>',
        submenu: this.contextSubmenuComponent,
        isHidden: this.lock
      },
      {
        title: '<i class="ri-lock-line m-r-8"></i>' + 'Lock'.trans('auditoriums/shape/context-menu/lock/option.Text'),
        action: () => {
          const imageEl = ImageManager.getImageElement(this._dataObject.guid);
          this.lock = true;
          CreatorActionsService.updateImages([{
            ...imageEl,
            lock: true
          }]);
        },
        isHidden: this.lock
      },
      {
        title: '<i class="ri-lock-unlock-line m-r-8"></i>' + 'Unlock'.trans('auditoriums/shape/context-menu/unlock/option.Text'),
        action: () => {
          const imageEl = ImageManager.getImageElement(this._dataObject.guid);
          this.lock = false;
          CreatorActionsService.updateImages([{
            ...imageEl,
            lock: false
          }]);
        },
        isHidden: !this.lock
      },
      {
        title: '<i class="ri-lock-unlock-line m-r-8"></i>' + 'Unlock all'.trans('auditoriums/shape/context-menu/unlock-all/option.Text'),
        action: () => {
          const imageInstances = AuditoriumShapeDirective.instances.filter(x => x.componentRef.instance instanceof ImageElementComponent);

          for (const instance of imageInstances) {
            instance.componentRef.instance.lock = false;
            const imageEl = ImageManager.getImageElement(instance.componentRef.instance._dataObject.guid);
            CreatorActionsService.updateImages([{
              ...imageEl,
              lock: false
            }]);
          }
        },
        isHidden: !this.lock
      },
      {
        title: '<i class="ri-delete-bin-line m-r-8 c-t-nok-400"></i>' + `<span class="c-t-nok-400">${'Delete element'.trans('auditoriums/shape/context-menu/delete/option.Text')}</span>`,
        action: () => CreatorActionsService.deleteImages([this._elementRef.nativeElement], {emitEvent: true}),
        isHidden: this.lock
      },
    ];

    this.alignSubmenuOptions = [
      {
        title: '<i class="ri-bring-to-front m-r-8"></i>' + 'Bring to front'.trans('auditoriums/shape/context-menu/bring-to-front/option.Text'),
        action: () => {
          const imageEl = ImageManager.getImageElement(this._dataObject.guid);
          this.layer = this.maxLayer + 1;

          CreatorActionsService.updateImages([{
            ...imageEl,
            layer: this.layer
          }]);
        }
      },
      {
        title: '<i class="ri-bring-forward m-r-8"></i>' + 'Bring forward'.trans('auditoriums/shape/context-menu/bring-forward/option.Text'),
        action: () => {
          const imageEl = ImageManager.getImageElement(this._dataObject.guid);
          const layer = imageEl.layer + 1;

          if (layer <= this.maxLayer + 1) {
            this.layer = layer;

            CreatorActionsService.updateImages([{
              ...imageEl,
              layer: this.layer
            }]);
          }
        }
      },
      {
        title: '<i class="ri-send-to-back m-r-8"></i>' + 'Send to back'.trans('auditoriums/shape/context-menu/send-to-back/option.Text'),
        action: () => {
          const imageEl = ImageManager.getImageElement(this._dataObject.guid);
          this.layer = 0;
          CreatorActionsService.updateImages([{
            ...imageEl,
            layer: this.layer
          }]);
        }
      },
      {
        title: '<i class="ri-send-backward m-r-8"></i>' + 'Send backward'.trans('auditoriums/shape/context-menu/send-backward/option.Text'),
        action: () => {
          const imageEl = ImageManager.getImageElement(this._dataObject.guid);
          const layer = imageEl.layer - 1;
          this.layer = layer > 0 ? layer : 0;

          CreatorActionsService.updateImages([{
            ...imageEl,
            layer: this.layer
          }]);
        }
      }
    ];

    this._cdr.detectChanges();
    this.contextMenuComponent.ngxContextMenu.open(event);
  }

  private get maxLayer(): number {
    return Math.max(...CreatorStateService.seatsPlan.images.filter(x => x.guid !== this._dataObject.guid).map(i => i.layer)) ?? 0;
  }
}
